import Papa from 'papaparse'

export interface ColumnDefinition {
  key: string
  label: string
  format?: (value: any, row?: any) => string
}

export interface ExportMetadata {
  title: string
  dateRange?: string
  filters?: string[]
  timestamp?: string
  /** Human-readable explanation of the query parameters (shown in PDF exports) */
  explanationText?: string
  /** Base64-encoded PNG of a chart to embed at the top of the PDF export */
  chartImage?: string
}

/**
 * Export table data to CSV file
 * @param data - Array of data objects to export
 * @param columns - Column definitions with keys, labels, and optional formatters
 * @param metadata - Optional metadata for filename and context
 */
export function exportToCsv(
  data: any[],
  columns: ColumnDefinition[],
  metadata: ExportMetadata
): void {
  if (!data || data.length === 0) {
    console.warn('No data to export')
    return
  }

  // Transform data to match column definitions
  const transformedData = data.map((row) => {
    const transformedRow: any = {}
    columns.forEach((col) => {
      const value = row[col.key]
      transformedRow[col.label] = col.format ? col.format(value, row) : value
    })
    return transformedRow
  })

  // Convert to CSV using papaparse
  const csv = Papa.unparse(transformedData, {
    columns: columns.map((col) => col.label),
    delimiter: ';', // Use semicolon for Norwegian Excel compatibility
  })

  // Generate filename
  const timestamp = new Date().toISOString().split('T')[0]
  const sanitizedTitle = metadata.title.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_')
  const filename = `${sanitizedTitle}_${timestamp}.csv`

  // Create download link
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }) // Add BOM for Excel
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
